import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScheduleBuilder, type ScheduleOutput } from './ScheduleBuilder';
import type { TriggerCreateInput } from '@/lib/scheduler-api';

interface TriggerEditorProps {
    initial?: Partial<TriggerCreateInput>;
    onSave: (input: TriggerCreateInput) => Promise<void>;
    saving?: boolean;
}

export function TriggerEditor({ initial, onSave, saving }: TriggerEditorProps) {
    const [name, setName] = useState(initial?.name ?? '');
    const [targetRef, setTargetRef] = useState(initial?.targetRef ?? '');
    const [targetMethod, setTargetMethod] = useState(initial?.targetMethod ?? '');
    const [concurrencyPolicy, setConcurrencyPolicy] = useState(initial?.concurrencyPolicy ?? 'forbid');
    const [misfirePolicy, setMisfirePolicy] = useState(initial?.misfirePolicy ?? 'fire_once_now');
    const [payloadStr, setPayloadStr] = useState(
        initial?.inputPayload ? JSON.stringify(initial.inputPayload, null, 2) : ''
    );
    const [schedule, setSchedule] = useState<ScheduleOutput>({
        triggerType: (initial?.triggerType as ScheduleOutput['triggerType']) ?? 'recurring',
        scheduleKind: (initial?.scheduleKind as ScheduleOutput['scheduleKind']) ?? 'cron',
        cronExpression: initial?.cronExpression ?? '',
        rruleExpression: initial?.rruleExpression ?? '',
        timezone: initial?.timezone ?? 'UTC',
        delayUntilAt: initial?.delayUntilAt,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        let inputPayload: Record<string, unknown> | undefined;
        if (payloadStr.trim()) {
            try {
                inputPayload = JSON.parse(payloadStr);
            } catch {
                alert('Invalid JSON payload');
                return;
            }
        }

        await onSave({
            name,
            triggerType: schedule.triggerType,
            scheduleKind: schedule.scheduleKind,
            targetRef,
            targetMethod: targetMethod || undefined,
            cronExpression: schedule.cronExpression || undefined,
            rruleExpression: schedule.rruleExpression || undefined,
            timezone: schedule.timezone,
            concurrencyPolicy,
            misfirePolicy,
            delayUntilAt: schedule.delayUntilAt,
            inputPayload,
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Basic Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>Name</Label>
                        <input
                            type="text"
                            required
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                            placeholder="Daily report generation"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Target</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>Capability Reference (FQCN)</Label>
                        <input
                            type="text"
                            required
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm font-mono"
                            placeholder="myapp/tool.reports.generator"
                            value={targetRef}
                            onChange={(e) => setTargetRef(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Method (optional)</Label>
                        <input
                            type="text"
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                            placeholder="run"
                            value={targetMethod}
                            onChange={(e) => setTargetMethod(e.target.value)}
                        />
                    </div>
                </CardContent>
            </Card>

            <ScheduleBuilder value={schedule} onChange={setSchedule} />

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Policies</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Concurrency</Label>
                        <select
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                            value={concurrencyPolicy}
                            onChange={(e) => setConcurrencyPolicy(e.target.value)}
                        >
                            <option value="allow">Allow</option>
                            <option value="forbid">Forbid</option>
                            <option value="queue">Queue</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <Label>Misfire</Label>
                        <select
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                            value={misfirePolicy}
                            onChange={(e) => setMisfirePolicy(e.target.value)}
                        >
                            <option value="fire_once_now">Fire Once Now</option>
                            <option value="catch_up_all">Catch Up All</option>
                            <option value="skip">Skip</option>
                        </select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Input Payload (JSON)</CardTitle>
                </CardHeader>
                <CardContent>
                    <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm font-mono"
                        placeholder='{"key": "value"}'
                        value={payloadStr}
                        onChange={(e) => setPayloadStr(e.target.value)}
                    />
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Trigger'}
                </Button>
            </div>
        </form>
    );
}
